<!-- eslint-disable
  vuejs-accessibility/mouse-events-have-key-events,
  vuejs-accessibility/no-static-element-interactions
-->
<template>
  <div
    ref="productCard"
    class="product-card stretched-link-container"
    :class="{
      [$class('loading')]: !product,
      'show-secondary-image': showSecondaryImage,
      'product-card__inline': inline,
      'product-card--has-carousel': showImageCarousel
    }"
    @focusin="handleRollover"
    @mouseenter="handleRollover"
  >
    <div class="product-card__top">
      <div class="product-card__image-container">
        <a
          v-if="cardType === 'product-cart-drawer'"
          class="product-card__image-link-cart-drawer"
          :href="product.url"
          :aria-label="product.title"
        />

        <div
          v-if="showImageCarousel"
          class="product-card__carousel"
        >
          <div
            ref="carouselTrack"
            class="product-card__carousel-track"
            @scroll.passive="handleCarouselScroll"
            @touchstart.passive="loadCarouselSlides(2)"
          >
            <!-- Duplicate of the title link, hidden from assistive technology -->
            <!-- eslint-disable-next-line vuejs-accessibility/anchor-has-content -->
            <a
              v-for="(image, index) of carouselImages"
              :key="image.src"
              class="product-card__carousel-slide"
              :href="product.url"
              aria-hidden="true"
              tabindex="-1"
              @click="handleProductClick"
            >
              <!-- Images only render once swiped to, to limit mobile memory -->
              <responsive-image
                class="product-card__primary-image"
                :image="index < loadedSlideCount ? image : {}"
                :lazyload="index === 0 && lazyload"
                :loaded-animation="index > 0 && loadedAnimation"
                ratio="4:5"
                :sizes="sizes"
              />
            </a>
          </div>

          <div
            class="product-card__carousel-dots"
            aria-hidden="true"
          >
            <span
              v-for="(image, index) of carouselImages"
              :key="`dot-${image.src}`"
              class="product-card__carousel-dot"
              :class="{ [$class('active')]: index === activeImageIndex }"
            />
          </div>
        </div>

        <responsive-image
          v-else
          class="product-card__primary-image"
          :image="product.featuredImage"
          :lazyload="lazyload"
          :loaded-animation="loadedAnimation"
          ratio="4:5"
          :sizes="sizes"
        />

        <responsive-image
          v-if="loadSecondaryImage && secondaryImage && !showImageCarousel"
          class="product-card__secondary-image"
          :image="secondaryImage"
          :padding="cardType === 'product-cart-drawer' ? true : false"
          ratio="4:5"
          :sizes="sizes"
          @image-loaded="showSecondaryImage = true"
        />

        <wishlist-button
          v-if="
            cardType === 'product-cart-drawer' &&
              product?.variants?.[0] &&
              product?.handle
          "
          class="product-card__wishlist product-card__wishlist--cart-drawer"
          :product="product"
          template="card"
          :variant="product.variants?.[0]"
        />
      </div>

      <product-form
        v-if="showQuickAdd && cardType !== 'product-cart-drawer' && !isMobile && product
          && !product.availabilityLoading"
        class="product-card__form"
        add-to-cart-modifiers="primary center"
        is-product-card
        :one-per-order-enabled="onePerOrderEnabled"
        :product="product"
        :section-id="`product-card-${product.id}`"
        :settings="formSettings"
        :variant="product.variants[0]"
      />

      <product-badges
        v-if="showBadges && product?.badges?.length > 0"
        class="product-card__badges"
        :badges="product?.badges"
      />

      <wishlist-button
        v-if="showWishlistIcon && product?.variants?.[0] && product?.handle"
        class="product-card__wishlist product-card__wishlist--small"
        :product="product"
        template="card"
        :variant="product.variants?.[0]"
      />
    </div>

    <pre-order-message
      v-if="cardType === 'product-cart-drawer' && isMobile"
      :product="product"
    />

    <div class="product-card__inner">
      <div class="product-card__body">
        <p
          v-if="promoTag"
          class="
            product-card__promo-tag
            text-other-label-s
            text-other-label-l-desktop
          "
          :class="{
            'product-card__promo-tag--alt': product?.promoTagAltStyle
          }"
          v-text="promoTag"
        />

        <a
          class="
            product-card__title
            stretched-link
            text-body-s-regular
            text-body-m-regular-desktop
          "
          :href="product.url"
          @click="handleProductClick"
          @focus="handleRollover"
          v-html="product.title"
        />

        <product-prices
          class="product-card__prices"
          :compare-at-price="product?.compareAtPrice?.amount"
          :loading="!product"
          :price="product?.price?.amount"
          :price-first="false"
          :varies="product?.price?.varies"
        />

        <product-rating
          v-if="showRatings"
          class="product-card__rating"
          :product="product"
          :visible="visible"
        />

        <div v-if="cardType === 'product-cart-drawer' && !isMobile && product">
          <product-form
            add-to-cart-modifiers="secondary block center"
            is-product-card
            :product="product"
            :one-per-order-enabled="product?.limits?.onePerOrder"
            :section-id="`product-card-${product.id}`"
            :settings="formSettings"
            :variant="product.variants[0]"
          />
        </div>
      </div>

      <wishlist-button
        v-if="showWishlistIcon && product?.variants?.[0] && product?.handle"
        class="product-card__wishlist"
        :product="product"
        template="card"
        :variant="product.variants?.[0]"
      />
    </div>

    <div
      v-if="showQuickAdd && isMobile && product && !product.availabilityLoading"
      class="product-card__footer"
    >
      <product-form
        class="product-card__form"
        add-to-cart-modifiers="secondary block center"
        is-product-card
        :product="product"
        :one-per-order-enabled="
          cardType === 'product-cart-drawer' ? product?.limits?.onePerOrder : onePerOrderEnabled
        "
        :section-id="`product-card-${product.id}`"
        :settings="formSettings"
        :variant="product.variants[0]"
      />
    </div>
  </div>

  <pre-order-message
    v-if="cardType === 'product-cart-drawer' && !isMobile"
    :product="product"
  />
</template>

<script>

/**
 * Vue: Product card (product-card)
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {String} [cardType] - Card type.
 * @param {Boolean} [lazyload] - Lazyload image, defaults to true.
 * @param {Boolean} [loadedAnimation] - Applies image loaded animation.
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @parma {String} [sizes] - Image sizes.
 * @param {Boolean} [showWishlistIcon] - Shows wishlist icon.
 * @param {Boolean} [showRatings] - Shows ratings.
 * @param {Boolean} [showBadges] - Shows badges.
 * @param {Boolean} [showQuickAdd] - Shows quick add button.
 * @param {Boolean} [inline] - Applies styling for grid version.
 * @param {Boolean} [onePerOrderEnabled] - one per order enabled.
 * @param {Boolean} [mobileImageCarousel] - Shows swipeable image carousel on
 * touch devices.
 *
 */
import { mapState } from 'vuex'

import PreOrderMessage from '~async/pre-order-message/pre-order-message'
import ProductBadges from '~async/product-badges/product-badges'
import ProductPrices from '~global/product-prices/product-prices'
import ProductRating from '~async/product-rating/product-rating'
import ProductForm from '~async/product-form/product-form'
import ResponsiveImage from '~global/images/responsive-image'
import WishlistButton from '~async/wishlist-button/wishlist-button'

import { setIntersectionObserver } from '~/helpers/general'
import { sendClickEvent } from '~/helpers/algolia-insights'
import { getProductImages, getSecondaryProductImage } from '~/helpers/product-images'

// Strip accidental "queryID:" prefix from queryID values.
function sanitiseQueryID(queryID) {
  if (typeof queryID === 'string' && queryID.startsWith('queryID:')) {
    return queryID.slice('queryID:'.length)
  }
  return queryID
}

// Algolia and Shopify can use different CDN URL formats, compare filenames.
function getImageFilename(src) {
  return typeof src === 'string'
    ? src.split('?')[0].split('/').pop()
    : ''
}

export default {
  name: 'ProductCard',

  components: {
    WishlistButton,
    PreOrderMessage,
    ProductBadges,
    ProductForm,
    ProductPrices,
    ProductRating,
    ResponsiveImage,
  },

  props: {
    cardType: {
      type: String,
      default: '',
    },
    lazyload: {
      type: Boolean,
      default: true,
    },
    loadedAnimation: {
      type: Boolean,
      default: true,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    sizes: {
      type: String,
      default: null,
    },
    showWishlistIcon: {
      type: Boolean,
      default: true,
    },
    showRatings: {
      type: Boolean,
      default: true,
    },
    showBadges: {
      type: Boolean,
      default: true,
    },
    showQuickAdd: {
      type: Boolean,
      default: true,
    },
    inline: {
      type: Boolean,
      default: false,
    },
    onePerOrderEnabled: {
      type: Boolean,
      default: false,
    },
    mobileImageCarousel: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      activeImageIndex: 0,
      fetchedImages: [],
      formSettings: {
        displayQuantitySelector: false,
      },
      fetchedSecondaryImage: null,
      hasCoarsePointer: false,
      loadedSlideCount: 1,
      loadSecondaryImage: false,
      requestedSecondaryImage: false,
      showSecondaryImage: false,
      visible: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Get the additional promo-tag for a product.
     * - If the product is a Collectors Exclusive, at that string from
     *   theme-strings
     * - Promo tag data from product metafields
     * @returns {String}
     */
    promoTag() {
      if (this.product?.limits?.collectors) {
        return this.$string('product.collectors_exclusive_promo_tag')
      }

      if (this.product?.limits?.collectorsEarly) {
        return this.$string('product.collectors_early_promo_tag')
      }

      if (this.product?.promoTag) {
        return this.product.promoTag
      }

      return ''
    },

    /**
     * Compute whether to display the Collectors Exclusive CTA.
     * @returns {Boolean}
     */
    showCollectorsExclusiveCta() {
      return !this.$variable('customer.isCollector') && this.product?.limits?.collectors
    },

    /**
     * Get secondary (rollover) image.
     * - Uses the product's secondary image when available.
     * - Algolia results only contain a single image so it falls back to the
     *   image requested on rollover.
     * @returns {Object|Null}
     */
    secondaryImage() {
      const image = this.product?.secondaryImage

      if (image?.src || image?.url) {
        return image
      }

      return this.fetchedSecondaryImage
    },

    /**
     * Get images for the mobile carousel.
     * - Starts with the Algolia featured image, followed by the additional
     *   images requested from Shopify.
     * @returns {Array}
     */
    carouselImages() {
      const featuredImage = this.product?.featuredImage
      const featuredSrc = featuredImage?.src || featuredImage?.url

      if (!featuredSrc || !this.fetchedImages.length) {
        return this.fetchedImages
      }

      const featuredFilename = getImageFilename(featuredSrc)

      const additionalImages = this.fetchedImages.filter((image) => {
        return getImageFilename(image.src) !== featuredFilename
      })

      return [featuredImage, ...additionalImages]
    },

    /**
     * Enable the swipeable image carousel.
     * - Uses the mobile breakpoint as some Android devices wrongly report
     *   `(hover: hover)`.
     * @returns {Boolean}
     */
    carouselEnabled() {
      return (
        this.mobileImageCarousel &&
        this.cardType !== 'product-cart-drawer' &&
        (this.isMobile || this.hasCoarsePointer)
      )
    },

    /**
     * Show the swipeable image carousel.
     * @returns {Boolean}
     */
    showImageCarousel() {
      return this.carouselEnabled && this.carouselImages.length > 1
    },
  },

  watch: {

    /**
     * Request carousel images if the carousel is enabled after the card is
     * visible (e.g. breakpoint state resolving or viewport resize).
     */
    carouselEnabled(enabled) {
      if (enabled && this.visible) {
        this.prefetchCarouselImages()
      }
    },

    /**
     * Watch product handle.
     * - Resets requested rollover image if card is re-used for another product.
     */
    'product.handle': function watchProductHandle() {
      this.fetchedSecondaryImage = null
      this.requestedSecondaryImage = false
      this.showSecondaryImage = false
      this.loadSecondaryImage = false
      this.fetchedImages = []
      this.activeImageIndex = 0
      this.loadedSlideCount = 1

      if (this.visible) {
        this.prefetchSecondaryImage()
        this.prefetchCarouselImages()
      }
    },
  },

  mounted() {
    this.hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches

    /**
     * Set an intersection observer to check when the product card is nearly in
     * view.
     * - Used to fetch the product reviews.
     */
    setIntersectionObserver({
      callback: this.handleIntersection,
      element: this.$refs.productCard,
      rootMargin: '200px 100px',
      threshold: 0.05,
    })
  },

  methods: {

    /**
     * Handle rollover interaction.
     * - Renders the secondary image on hover/focus.
     * - Requests the secondary image when the product data doesn't include one
     *   (Algolia hits only contain a single image).
     */
    async handleRollover() {
      await this.requestSecondaryImage()

      if (this.secondaryImage) {
        this.loadSecondaryImage = true
      }
    },

    /**
     * Prefetch secondary image once the card is in view.
     * - Requested and rendered before hover so the rollover is instant.
     * - Only on devices which support hover as the rollover isn't displayed on
     *   touch devices.
     */
    async prefetchSecondaryImage() {
      if (this.carouselEnabled || !window.matchMedia('(hover: hover)').matches) {
        return
      }

      await this.requestSecondaryImage()

      if (this.secondaryImage) {
        this.loadSecondaryImage = true
      }
    },

    /**
     * Request secondary image when the product data doesn't include one.
     * - Algolia hits only contain a single image.
     * - Responses are cached by the helper so each product is only requested
     *   once per session.
     */
    async requestSecondaryImage() {
      if (
        this.secondaryImage ||
        this.requestedSecondaryImage ||
        this.cardType === 'product-cart-drawer' ||
        !this.product?.handle
      ) {
        return
      }

      this.requestedSecondaryImage = true
      this.fetchedSecondaryImage = await getSecondaryProductImage(this.product.handle)
    },

    /**
     * Request the product's images for the mobile carousel.
     * - Algolia hits only contain a single image so the rest are requested from
     *   Shopify once the card is in view.
     */
    async prefetchCarouselImages() {
      if (!this.carouselEnabled || !this.product?.handle || this.fetchedImages.length) {
        return
      }

      const { handle } = this.product
      const images = await getProductImages(handle)

      // Card may have been re-used for another product while requesting
      if (this.product?.handle === handle) {
        this.fetchedImages = images
      }
    },

    /**
     * Update active dot based on carousel scroll position.
     */
    handleCarouselScroll() {
      const track = this.$refs.carouselTrack

      if (!track?.clientWidth) {
        return
      }

      this.activeImageIndex = Math.round(track.scrollLeft / track.clientWidth)
      this.loadCarouselSlides(this.activeImageIndex + 2)
    },

    /**
     * Render carousel slide images up to the given count.
     * @param {Number} count - Number of slides to render images for.
     */
    loadCarouselSlides(count) {
      if (count > this.loadedSlideCount) {
        this.loadedSlideCount = Math.min(count, this.carouselImages.length)
      }
    },

    /**
     * Check if the product card is intersecting and update the `visible` data.
     * - Observer is unobserved once visible to prevent multiple triggering.
     * @param entries - IntersectionObserver entries
     * @param observer - IntersectionObserver instance
     */
    handleIntersection(entries, observer) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return
        }

        this.visible = true
        this.prefetchSecondaryImage()
        this.prefetchCarouselImages()
        observer.unobserve(entry.target)
      })
    },

    /**
     * Handle product card click to send Algolia click event.
     * - Only fires when product has Algolia query context.
     * - Stores query in sessionStorage for PDP view/conversion tracking.
     * - Stores click data for Algolia app's built-in
     *   web pixel.
     */
    handleProductClick() {
      // eslint-disable-next-line no-underscore-dangle
      const queryID = sanitiseQueryID(this.product?.__queryID)
      // eslint-disable-next-line no-underscore-dangle
      const indexName = this.product?.__indexName
      // eslint-disable-next-line no-underscore-dangle
      const position = this.product?.__position

      if (!queryID || !indexName) {
        return
      }

      sendClickEvent({
        indexName,
        objectID: this.product.variantId || this.product.objectID,
        position,
        queryID,
      })

      // Store Algolia context for the product page
      try {
        sessionStorage.setItem('algolia_query_context', JSON.stringify({
          queryID,
          indexName,
          objectID: String(this.product.variantId || this.product.objectID),
          position,
        }))
      } catch {
        // Silently fail if sessionStorage is unavailable
      }
    },
  },
}
</script>
